import { ElementType, MouseEvent as ReactMouseEvent, ReactElement, useMemo } from 'react';
import { Box, Flex, Tooltip } from '@chakra-ui/react';
import { Link } from 'react-router-dom';

import FavIcon from 'components/Icons/FavIcon';
import { radius } from 'styles/revampTokens';

export interface BlockNavigationDropdownItem {
  id: string | number;
  title: string;
  source?: any;
  href?: string | null;
}

export interface BlockNavigationClickOptions {
  openInNewTab?: boolean;
}

interface BlockNavigationDropdownProperties {
  blocks: BlockNavigationDropdownItem[];
  onBlockClick: (block: BlockNavigationDropdownItem, options?: BlockNavigationClickOptions) => void;
  onViewAllBlocksClick: (options?: BlockNavigationClickOptions) => void;
  viewAllHref?: string | null;
}

const LINK_RESET_STYLE = {
  textDecoration: 'none',
  color: 'inherit',
  width: '100%',
} as const;

const isModifiedClick = (event: ReactMouseEvent) =>
  event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0;

const DropdownBox = Box as ElementType;
const DropdownFlex = Flex as ElementType;
const DropdownTooltip = Tooltip as ElementType;

const SECTION_TITLE_STYLE = {
  color: '#121826',
  fontSize: '14px',
  fontWeight: '600',
  lineHeight: '20px',
};

const ROW_TEXT_STYLE = {
  color: '#121826',
  fontSize: '14px',
  fontWeight: '400',
  lineHeight: '20px',
};

const isFavouriteBlock = (block: any): boolean =>
  Boolean(block?.favourite || block?.isFavourite || block?.is_favourite || block?.isFavourtie);

const ViewAllBlocksIcon = () => (
  <svg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 20 20' fill='none'>
    <path
      d='M11.6667 5.0083L10.4917 6.1833L13.4667 9.16663H3.33334V10.8333H13.4667L10.4917 13.8166L11.6667 14.9916L16.6667 9.99996L11.6667 5.0083Z'
      fill='#8B5CF6'
    />
  </svg>
);

const BlockNavigationDropdown = ({
  blocks,
  onBlockClick,
  onViewAllBlocksClick,
  viewAllHref,
}: BlockNavigationDropdownProperties): ReactElement => {
  const { favouriteBlocks, otherBlocks } = useMemo(() => {
    return blocks.reduce(
      (accumulator, block) => {
        if (isFavouriteBlock(block.source)) {
          accumulator.favouriteBlocks.push(block);
        } else {
          accumulator.otherBlocks.push(block);
        }

        return accumulator;
      },
      {
        favouriteBlocks: [] as BlockNavigationDropdownItem[],
        otherBlocks: [] as BlockNavigationDropdownItem[],
      },
    );
  }, [blocks]);

  const renderRows = (items: BlockNavigationDropdownItem[], showFavouriteIcon = false) => (
    <DropdownFlex
      alignSelf='stretch'
      flexDirection='column'
      justifyContent='flex-start'
      alignItems='flex-start'
      gap='2px'
    >
      {items.map((block) => (
        <DropdownFlex
          key={block.id}
          as={block.href ? Link : undefined}
          to={block.href || undefined}
          style={block.href ? LINK_RESET_STYLE : undefined}
          alignSelf='stretch'
          px='6px'
          py='6px'
          bg='white'
          justifyContent='flex-start'
          alignItems='center'
          gap='8px'
          cursor='pointer'
          borderRadius={radius.control}
          _hover={{ bg: '#F5F3FF' }}
          sx={{
            '&:hover .block-navigation-dropdown-row-title': {
              color: '#8B5CF6',
            },
          }}
          onClick={(event: ReactMouseEvent) => {
            if (isModifiedClick(event)) {
              return;
            }
            event.stopPropagation();
            // Real links update the URL immediately. Only fall back to the
            // click handler when this row has no href.
            if (!block.href) {
              event.preventDefault();
              onBlockClick(block, { openInNewTab: false });
            }
          }}
          onContextMenu={(event: ReactMouseEvent) => {
            event.preventDefault();
            event.stopPropagation();
            onBlockClick(block, { openInNewTab: true });
          }}
        >
          {showFavouriteIcon && (
            <DropdownBox width='16px' height='16px' flexShrink={0}>
              <FavIcon />
            </DropdownBox>
          )}
          <DropdownTooltip label={block.title} hasArrow placement='right'>
            <DropdownBox
              className='block-navigation-dropdown-row-title'
              flex='1 1 0'
              noOfLines={1}
              {...ROW_TEXT_STYLE}
            >
              {block.title}
            </DropdownBox>
          </DropdownTooltip>
        </DropdownFlex>
      ))}
    </DropdownFlex>
  );

  return (
    <DropdownFlex
      minW='260px'
      maxW='320px'
      maxH='320px'
      bg='white'
      border='1px solid #D1D6DD'
      boxShadow='0px 8px 24px rgba(15, 23, 42, 0.16)'
      overflow='hidden'
      borderRadius={radius.overlay}
      flexDirection='column'
      justifyContent='flex-start'
      alignItems='flex-start'
      p='8px'
    >
      <DropdownFlex
        alignSelf='stretch'
        flex='1 1 auto'
        minH={0}
        overflowY='auto'
        flexDirection='column'
        gap='6px'
      >
        {favouriteBlocks.length > 0 && (
          <DropdownFlex
            alignSelf='stretch'
            flexDirection='column'
            justifyContent='flex-start'
            alignItems='flex-start'
            gap='2px'
          >
            <DropdownFlex
              alignSelf='stretch'
              pt='2px'
              pb='4px'
              px='6px'
              bg='white'
              justifyContent='flex-start'
              alignItems='center'
              gap='8px'
            >
              <DropdownBox {...SECTION_TITLE_STYLE}>Favourite Blocks</DropdownBox>
            </DropdownFlex>
            {renderRows(favouriteBlocks, true)}
          </DropdownFlex>
        )}

        {otherBlocks.length > 0 && (
          <DropdownFlex
            alignSelf='stretch'
            flexDirection='column'
            justifyContent='flex-start'
            alignItems='flex-start'
            gap='2px'
          >
            <DropdownFlex
              alignSelf='stretch'
              pt={favouriteBlocks.length > 0 ? '6px' : '2px'}
              pb='4px'
              px='6px'
              bg='white'
              justifyContent='flex-start'
              alignItems='center'
              gap='8px'
            >
              <DropdownBox {...SECTION_TITLE_STYLE}>All Other Blocks</DropdownBox>
            </DropdownFlex>
            {renderRows(otherBlocks)}
          </DropdownFlex>
        )}
      </DropdownFlex>

      <DropdownFlex
        as={viewAllHref ? Link : undefined}
        to={viewAllHref || undefined}
        style={viewAllHref ? LINK_RESET_STYLE : undefined}
        alignSelf='stretch'
        mt='6px'
        px='6px'
        py='6px'
        bg='white'
        borderTop='1px solid #D1D6DD'
        justifyContent='flex-start'
        alignItems='center'
        gap='8px'
        cursor='pointer'
        flexShrink={0}
        borderRadius={radius.control}
        _hover={{ bg: '#F5F3FF' }}
        onClick={(event: ReactMouseEvent) => {
          if (isModifiedClick(event)) {
            return;
          }
          event.stopPropagation();
          if (!viewAllHref) {
            event.preventDefault();
          }
          onViewAllBlocksClick({ openInNewTab: false });
        }}
        onContextMenu={(event: ReactMouseEvent) => {
          event.preventDefault();
          event.stopPropagation();
          onViewAllBlocksClick({ openInNewTab: true });
        }}
      >
        <DropdownBox color='#8B5CF6' fontSize='14px' fontWeight='600' lineHeight='20px'>
          View all Blocks
        </DropdownBox>
        <DropdownBox width='16px' height='16px'>
          <ViewAllBlocksIcon />
        </DropdownBox>
      </DropdownFlex>
    </DropdownFlex>
  );
};

export default BlockNavigationDropdown;
